import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, PanelLeftOpen } from 'lucide-react'
import UtmMap from '@/components/utm/map/UtmMap'
import UtmSidebar from '@/components/utm/map/UtmSidebar'
import UtmSelectionResultsPanel from '@/components/utm/map/UtmSelectionResultsPanel'
import UtmSelectionToolbar from '@/components/utm/map/UtmSelectionToolbar'
import { Sheet, SheetContent } from '@/components/ui/sheet'

export default function UtmMapPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isDesktopSidebarHidden, setIsDesktopSidebarHidden] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const initialSelection = useMemo(() => {
    const candidate = (location.state as any)?.flightPlanSelection
    if (!candidate?.geometry) {
      return null
    }
    return {
      geometry: candidate.geometry as GeoJSON.Geometry,
      selectionMode: candidate.selectionMode as 'polygon' | 'radius' | 'corridor' | undefined,
    }
  }, [location.state])

  const returnTo = useMemo(() => {
    const candidate = (location.state as any)?.returnTo
    return typeof candidate === 'string' && candidate.length > 0 ? candidate : null
  }, [location.state])

  return (
    <div className="h-full min-h-0 flex overflow-hidden">
      {!isMobile && (
        <UtmSidebar
          hidden={isDesktopSidebarHidden}
          onToggleHidden={() => setIsDesktopSidebarHidden((prev) => !prev)}
        />
      )}
      {isMobile && (
        <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <SheetContent side="left" className="w-[88vw] max-w-[22rem] p-0">
            <UtmSidebar hidden={false} showHideToggle={false} className="max-w-none" />
          </SheetContent>
        </Sheet>
      )}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {returnTo && (
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            className="absolute right-3 top-3 z-30 inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-[#3a3a3a] shadow-sm transition-colors hover:bg-[#f6f6f6] sm:text-sm lg:right-20"
            aria-label="Back to application"
          >
            <ArrowLeft size={16} />
            Back to application
          </button>
        )}
        {(!isMobile && isDesktopSidebarHidden) || (isMobile && !isMobileSidebarOpen) ? (
          <button
            type="button"
            onClick={() => {
              if (isMobile) {
                setIsMobileSidebarOpen(true)
                return
              }
              setIsDesktopSidebarHidden(false)
            }}
            className="absolute left-3 top-3 z-30 inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-[#3a3a3a] shadow-sm transition-colors hover:bg-[#f6f6f6] sm:px-4 sm:text-sm"
            aria-label="Show sidebar"
          >
            <PanelLeftOpen size={18} />
            MyMaps
          </button>
        ) : null}
        <UtmMap
          initialSelection={initialSelection}
          locationSearchLeftPx={isMobile ? 8 : isDesktopSidebarHidden ? 170 : 16}
          sidebarHidden={!isMobile && isDesktopSidebarHidden}
        />
        <UtmSelectionResultsPanel />
        <UtmSelectionToolbar />
      </div>
    </div>
  )
}
